import styles from "./Modals.module.scss";
import React, { useEffect, useRef, useState } from "react";
import { DESTINATION_INTEGRATION_TYPES, MessageDestination } from "@whatslly/types/messages-server/destinations";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { Field, FieldProps, FormikErrors, FormikProps } from "formik";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { useTranslation } from "react-i18next";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import { FormCheckBox } from "@whatslly/ui-components/v2/FormElements/FormCheckBox";
import { FormSelect } from "@whatslly/ui-components/v2/FormElements/FormSelect";
import { TIME_ZONE_OPTIONS } from "@whatslly/commons/timeZoneOptions";
import { CronScheduler } from "../components/Cron";
import { useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { DestinationsStore } from "../DestinationsStore";
import type { DestinationFormProps, FormDestinationDataType } from "../utils/DestinationsListTypes";
import classNames from "classnames";

const BASE_DATA = {
  type: "proofpoint" as const,
  integrationType: DESTINATION_INTEGRATION_TYPES.MESSAGES,
  settings: {
    rcpt: "",
    msgDateTimeZone: "UTC",
  },
  batchJob: {} as MessageDestination["batchJob"],
};

type FormDataType = FormDestinationDataType<"proofpoint">;
const TIME_ZONE_DROPDOWN_OPTIONS = [...TIME_ZONE_OPTIONS].map((value) => ({ value, label: value }));

export const ProofPointModalForm: React.FC<DestinationFormProps<"proofpoint">> = ({ onSubmitForm, settings, batchJob }) => {
  const { t } = useTranslation();
  const formikContext = useRef<FormikProps<FormDataType>>(null);
  const store = useLocalStoreWithInjectedDependencies(DestinationsStore);
  const [msgDateTimeZoneValue, setMsgDateTimeZoneValue] = useState(settings?.msgDateTimeZone ?? "UTC");
  const validate = async (values: FormDataType) => {
    const errors: FormikErrors<PartialRecord<keyof FormDataType, string>> = {};
    if (!values?.rcpt) errors.rcpt = t("field_is_required");
    return errors;
  };

  const getFormValues = () => {
    const data = (formikContext.current?.values ?? {}) as FormDataType;
    const result: any = { ...BASE_DATA, settings: { ...data, msgDateTimeZone: msgDateTimeZoneValue } };
    result.batchJob = { sync: result.settings.sync, cronExpression: result.settings.cronExpression };
    delete result.settings["cronExpression"];
    delete result.settings["sync"];
    return result;
  };

  useEffect(() => {
    validate(getFormValues().settings);
  }, []);

  const onSubmit = async () => {
    store.testConnection(t, getFormValues());
    const res = await store.testConnectionPromise;
    if (res?.success) {
      onSubmitForm(getFormValues());
    }
  };

  return (
    <FormElement
      initialValues={
        settings
          ? {
              ...settings,
              ...(batchJob ?? BASE_DATA.batchJob),
            }
          : { ...BASE_DATA.settings, ...BASE_DATA.batchJob }
      }
      validate={validate}
      onSubmit={onSubmit}
      formikRef={formikContext}
    >
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.rcpt")} required />
        <FormTextInput name={"rcpt"} paddingInline="none" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.msg_date_timezone")} />
        <FormSelect
          isSearchable={true}
          name={`msgDateTimeZone`}
          paddingInline="none"
          options={TIME_ZONE_DROPDOWN_OPTIONS}
          onChange={(selectedOption: (typeof TIME_ZONE_DROPDOWN_OPTIONS)[number]) => setMsgDateTimeZoneValue(selectedOption?.value)}
          defaultValue={
            settings?.msgDateTimeZone
              ? {
                  label: settings.msgDateTimeZone,
                  value: settings.msgDateTimeZone,
                }
              : { label: "UTC", value: "UTC" }
          }
          isClearable={false}
          required
        />
      </div>
      <div className={classNames(styles.field, styles.fieldCheckbox)}>
        <Label className={styles.filedLabel} text={t("destinations_page.store_media")} />
        <FormCheckBox name={"storeMedia"} paddingInline="none" />
      </div>
      <div className={classNames(styles.field, styles.fieldCheckbox)}>
        <Label className={styles.filedLabel} text={t("destinations_page.batch.title")} />
        <FormCheckBox name={"sync"} paddingInline="none" />
      </div>
      <Field
        name={`defaultValue`}
        children={(fieldData: FieldProps<string, FormDataType>) => {
          return (
            fieldData.form?.values?.["sync"] && (
              <CronScheduler
                value={batchJob?.cronExpression ?? "0 0 * * *"}
                onChange={(cron: string) => formikContext.current?.setFieldValue("cronExpression", cron)}
              />
            )
          );
        }}
      />

      <ModalButtons
        submitText={t("destinations_page.save")}
        testId={"destination-ok-button"}
        onSubmit={() => formikContext.current?.submitForm()}
        hideCancel={true}
      />
    </FormElement>
  );
};
